import {
	formatIstDateTime,
	formatVettingId,
	type Pagination as PaginationMeta,
	parseTimestamp,
	type WorkStatus,
	workStatusLabel,
} from "@anthra/platform/domain"
import { toDisplayable } from "@anthra/platform/errors"
import { buttonVariants } from "@anthra/ui/button-variants"
import { cn } from "@anthra/ui/cn"
import { FloatingAction } from "@anthra/ui/floating-action"
import { Skeleton } from "@anthra/ui/skeleton"
import { ToggleGroup, ToggleGroupItem } from "@anthra/ui/toggle-group"
import {
	CardTitle,
	Description,
	Meta,
	Overline,
	PageTitle,
	SectionTitle,
} from "@anthra/ui/typography"
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import { FileTextIcon, TriangleAlertIcon } from "lucide-react"
import { useState } from "react"

import { copy } from "@/copy/my-vetting-requests"
import { ItemBadge } from "@/features/activity/item-badge"
import { PersonalisedServicesBanner } from "@/features/content/personalised-services-banner"
import { useMyVettingRequests } from "@/features/vetting/use-vetting"

/**
 * My Vetting Requests (US-00-004.7/.8/.9).
 *
 * Built against screens/my-vetting-requests.html: the status chips and the
 * request rows. Only this user's requests, under any page or filter
 * (US-00-006.2). The empty state explains what vetting IS and offers the create
 * action rather than showing an error (US-00-004.9), and the Personalised
 * Services banner appears alongside it (US-00-005.10).
 *
 * NO SEARCH BOX, for the same reason as My Queries: `MyVettingFilters` — and the
 * API behind it — takes a status only.
 */
export const Route = createFileRoute("/_app/vetting/")({
	component: MyVettingRequests,
})

function MyVettingRequests() {
	const navigate = useNavigate()
	const [status, setStatus] = useState<WorkStatus | undefined>(undefined)
	const [page, setPage] = useState(1)
	const requests = useMyVettingRequests(status ? { status } : {}, page)

	const all = useMyVettingRequests({}, 1)
	const open = useMyVettingRequests({ status: "open" }, 1)
	const inProgress = useMyVettingRequests({ status: "in_progress" }, 1)
	const answered = useMyVettingRequests({ status: "answered" }, 1)
	const closed = useMyVettingRequests({ status: "closed" }, 1)

	const CHIPS: { status: WorkStatus | undefined; label: string; count: number | undefined }[] = [
		{ status: undefined, label: "All", count: all.data?.pagination.totalCount },
		{ status: "open", label: workStatusLabel("open"), count: open.data?.pagination.totalCount },
		{
			status: "in_progress",
			label: workStatusLabel("in_progress"),
			count: inProgress.data?.pagination.totalCount,
		},
		{
			status: "answered",
			label: workStatusLabel("answered"),
			count: answered.data?.pagination.totalCount,
		},
		{
			status: "closed",
			label: workStatusLabel("closed"),
			count: closed.data?.pagination.totalCount,
		},
	]

	function selectChip(next: WorkStatus | undefined): void {
		setStatus(next)
		setPage(1)
	}

	return (
		<div
			className="flex flex-col gap-6"
			data-screen="my-vetting-requests"
		>
			<header className="flex flex-wrap items-start justify-between gap-3">
				<div className="flex min-w-0 flex-col gap-1">
					<PageTitle>My vetting requests</PageTitle>
					<Description>
						Drafts you have sent for a second opinion. Only you can see these.
					</Description>
				</div>
				<div className="shrink-0">
					<Link
						className={cn(buttonVariants({ variant: "primary" }))}
						to="/vetting/new"
					>
						New vetting request
					</Link>
				</div>
			</header>

			<div className="flex flex-col gap-3">
				<div className="flex flex-wrap items-center justify-between gap-3">
					{/* One choice of five, matching My Questions. The chips carried
					    aria-pressed, which announces five independent toggles rather
					    than one filter with a current value. */}
					<ToggleGroup
						type="single"
						value={status ?? "all"}
						onValueChange={next => {
							if (next === "") return
							selectChip(next === "all" ? undefined : (next as WorkStatus))
						}}
						aria-label="Filter by status"
					>
						{CHIPS.map(chip => (
							<ToggleGroupItem
								key={chip.label}
								value={chip.status ?? "all"}
							>
								{chip.label}
								<span className="text-meta opacity-70">{chip.count ?? "—"}</span>
							</ToggleGroupItem>
						))}
					</ToggleGroup>
					{requests.data ? (
						<p className="text-meta text-muted">
							{requests.data.pagination.totalCount} request
							{requests.data.pagination.totalCount === 1 ? "" : "s"}
						</p>
					) : null}
				</div>
			</div>

			{requests.isPending ? (
				<>
					<p
						className="sr-only"
						role="status"
					>
						Loading your vetting requests.
					</p>
					<ul
						className="flex flex-col gap-2"
						aria-hidden="true"
					>
						{["a", "b", "c"].map(key => (
							<li
								key={`skel-${key}`}
								className="rounded-card border-border bg-card shadow-card border p-4"
							>
								<div className="flex items-center gap-3">
									<Skeleton className="rounded-pill h-6 w-20" />
									<Skeleton className="h-4 flex-1" />
								</div>
							</li>
						))}
					</ul>
				</>
			) : null}

			{requests.isError ? (
				<div
					className="rounded-card border-status-breach-border bg-status-breach-bg flex items-start gap-3 border p-4"
					role="alert"
				>
					<TriangleAlertIcon
						className="text-status-breach-fg size-5 shrink-0"
						aria-hidden="true"
					/>
					<div className="flex min-w-0 flex-col gap-1">
						<CardTitle as="h2">{toDisplayable(requests.error).title}</CardTitle>
						<Description>{toDisplayable(requests.error).message}</Description>
						<div className="mt-1 flex flex-wrap gap-2">
							<button
								type="button"
								className={cn(buttonVariants({ variant: "primary" }))}
								onClick={() => {
									void requests.refetch()
								}}
							>
								Try again
							</button>
							<Link
								className={cn(buttonVariants({ variant: "secondary" }))}
								to="/home"
							>
								Go to Home
							</Link>
						</div>
					</div>
				</div>
			) : null}

			<div className="flex flex-col gap-4">
				<div>
					{requests.data && requests.data.data.length === 0 ? (
						<div className="flex flex-col items-center gap-3 py-10 text-center">
							<span className="bg-subtle text-primary rounded-circle flex size-14 items-center justify-center">
								<FileTextIcon
									className="size-7 shrink-0"
									aria-hidden="true"
								/>
							</span>
							<div className="flex flex-col items-center gap-2">
								<SectionTitle as="h2">
									{status === undefined
										? copy.empty.emptyText
										: `No ${workStatusLabel(status)} vetting requests`}
								</SectionTitle>
								<Description className="max-w-prose">{copy.empty.btn2}</Description>
							</div>
							{status === undefined ? (
								<div className="border-border flex w-full max-w-sm flex-col gap-2 border-t pt-4 text-left">
									<Overline>How vetting works</Overline>
									<ol className="flex flex-col gap-2">
										<li className="text-description text-subtle-foreground flex items-start gap-2">
											<span className="bg-subtle text-primary rounded-circle text-meta flex size-5 shrink-0 items-center justify-center font-semibold">
												1
											</span>
											<p>Upload the notice and the reply you have drafted.</p>
										</li>
										<li className="text-description text-subtle-foreground flex items-start gap-2">
											<span className="bg-subtle text-primary rounded-circle text-meta flex size-5 shrink-0 items-center justify-center font-semibold">
												2
											</span>
											<p>An expert on the panel reviews both side by side.</p>
										</li>
										<li className="text-description text-subtle-foreground flex items-start gap-2">
											<span className="bg-subtle text-primary rounded-circle text-meta flex size-5 shrink-0 items-center justify-center font-semibold">
												3
											</span>
											<p>You get their written feedback before you file.</p>
										</li>
									</ol>
								</div>
							) : null}
							<div className="flex flex-wrap justify-center gap-2">
								<Link
									className={cn(buttonVariants({ variant: "primary" }))}
									to="/vetting/new"
								>
									Create vetting request
								</Link>
								<Link
									className={cn(buttonVariants({ variant: "secondary" }))}
									to="/queries/new"
								>
									Ask a question instead
								</Link>
							</div>
						</div>
					) : null}

					{requests.data && requests.data.data.length > 0 ? (
						<ul className="flex flex-col gap-2">
							{requests.data.data.map(request => {
								const submitted = parseTimestamp(request.submittedAt)
								return (
									<li
										key={request.id}
										// No highlight ring: new activity is shown by the row's count
										// badge (NOTASK-1), and the old "answered in the last 24 hours"
										// ring was removed at the client's request.
										className="rounded-card border-border bg-card shadow-card relative flex cursor-pointer flex-col gap-2 border p-4 sm:flex-row sm:items-start sm:justify-between sm:gap-4"
									>
										<Link
											className="focus-visible:ring-ring absolute inset-0 z-10 rounded-[inherit] focus-visible:ring-2 focus-visible:outline-none"
											to="/vetting/$vettingId"
											params={{ vettingId: String(request.sequence) }}
											aria-label={`Open vetting request ${formatVettingId(request.sequence)}: ${request.title}`}
										/>
										<div className="flex min-w-0 flex-col gap-1">
											{/* The reference stays its own element rather than being
											    folded into the line: it is the thing a person reads
											    out over the phone, and it is what they search for. */}
											<Meta className="flex flex-wrap items-center gap-x-1.5">
												<span className="text-foreground font-semibold">
													{formatVettingId(request.sequence)}
												</span>
												<span aria-hidden="true">·</span>
												<span>
													Submitted{" "}
													{submitted ? formatIstDateTime(submitted) : "—"}
												</span>
											</Meta>
											<div className="flex min-w-0 items-center">
												<CardTitle
													className={cn(
														"max-w-full truncate",
														request.hasUnseen && "font-bold",
													)}
												>
													{request.title}
												</CardTitle>
												<ItemBadge count={request.unseenCount} />
											</div>
										</div>
										<div className="shrink-0">
											<Description className="capitalize">
												{request.status}
											</Description>
										</div>
									</li>
								)
							})}
						</ul>
					) : null}

					{requests.data && requests.data.data.length > 0 ? (
						<VettingPager
							pagination={requests.data.pagination}
							page={page}
							onPageChange={setPage}
						/>
					) : null}
				</div>

				{/* FULL WIDTH, like Home. This was an `lg:w-80 lg:shrink-0` aside
				    inside a COLUMN flex, so it rendered as a 320px block with the
				    rest of the row empty beside it rather than as a sidebar. */}
				<PersonalisedServicesBanner screen="my_vetting_requests" />
			</div>
			{/* The primary create, where a thumb is. The head button above is
			    hidden below lg, so there is one primary action per breakpoint
			    rather than two competing ones. */}
			<FloatingAction
				label="New vetting request"
				onClick={() => {
					void navigate({ to: "/vetting/new" })
				}}
			/>
		</div>
	)
}

/**
 * screens/my-vetting-requests.html's own pager — NOT the shared
 * `<Pagination>` used by My queries: this screen names the same numbered
 * control differently (`.page-btn`, not `.pager__btn`), and carries its own
 * compact prev/next pair for narrow widths, `.pager__mobile`, alongside the
 * numbered row rather than in place of it (`.pager__pages` is hidden below
 * the desktop breakpoint by this screen's own CSS, not by this component).
 *
 * NUMBERED PAGINATION ONLY, ON EVERY WIDTH — no pull-to-refresh and no
 * infinite scroll here even in the narrow layout: those are the Expo app's
 * own pattern (US-00-004.8), and "web" in that criterion means this app, not
 * a viewport size.
 */
function VettingPager({
	pagination,
	page,
	onPageChange,
}: {
	pagination: PaginationMeta
	page: number
	onPageChange: (page: number) => void
}) {
	const totalPages =
		pagination.totalPages ??
		Math.max(1, Math.ceil(pagination.totalCount / Math.max(1, pagination.limit)))
	if (totalPages <= 1) return null

	const start = Math.max(1, Math.min(page - 2, totalPages - 4))
	const end = Math.min(totalPages, start + 4)
	const pages: number[] = []
	for (let candidate = start; candidate <= end; candidate += 1) pages.push(candidate)

	return (
		<nav
			className="border-border flex flex-wrap items-center justify-between gap-3 border-t pt-4"
			aria-label="Pagination"
		>
			<p className="text-meta text-muted">
				{pagination.totalCount} in total · page {page} of {totalPages}
			</p>

			<div
				className="flex items-center gap-2 sm:hidden"
				aria-hidden={pages.length > 0 ? undefined : "true"}
			>
				<button
					type="button"
					className={cn(buttonVariants({ variant: "secondary" }))}
					disabled={page <= 1}
					onClick={() => {
						onPageChange(page - 1)
					}}
				>
					Previous
				</button>
				<button
					type="button"
					className={cn(buttonVariants({ variant: "secondary" }))}
					disabled={page >= totalPages}
					onClick={() => {
						onPageChange(page + 1)
					}}
				>
					Next
				</button>
			</div>

			<div className="hidden items-center gap-1 sm:flex">
				<button
					type="button"
					className={cn(buttonVariants({ variant: "secondary", size: "sm" }), "min-w-9")}
					aria-label="Previous page"
					disabled={page <= 1}
					onClick={() => {
						onPageChange(page - 1)
					}}
				>
					‹
				</button>
				{pages.map(candidate => (
					<button
						key={candidate}
						type="button"
						className={cn(
							buttonVariants({ variant: "secondary", size: "sm" }),
							"min-w-9",
						)}
						aria-current={candidate === page ? "page" : undefined}
						onClick={() => {
							onPageChange(candidate)
						}}
					>
						{candidate}
					</button>
				))}
				<button
					type="button"
					className={cn(buttonVariants({ variant: "secondary", size: "sm" }), "min-w-9")}
					aria-label="Next page"
					disabled={page >= totalPages}
					onClick={() => {
						onPageChange(page + 1)
					}}
				>
					›
				</button>
			</div>
		</nav>
	)
}
